import { ApolloClient, InMemoryCache } from "@apollo/client/core"
import { onError } from "@apollo/client/link/error"

import { responseTransformer } from "shared/api/defaultResponseTransformer"
import { deleteMutationResponseTransformer } from "shared/api/deleteMutationResponseTransformer"
import { healthResponseTransformer } from "shared/api/healthResponseTransformer"
import { noTransform } from "shared/api/noTransform"
import { priceResponseTransformer } from "shared/api/price/priceResponseTransformer"
import { productPriceResponseTransformer } from "shared/api/price/productPriceResponseTransformer"
import { request } from "shared/api/request"
import { bodySerializers } from "shared/api/serializer/bodySerializers"
import { userSpaceResponseTransformer } from "shared/api/user-space/userRolesResponseTransformer"
import { GraphQLResponseError } from "shared/types/error/GraphQLResponseError"

import { RestLink } from "apollo-link-rest"

import { attachmentAcceptedFileTypesResponseTransformer } from "entities/attachments/attachmentAcceptedFileTypesResponseTransformer"
import { attachmentEntitiesResponseTransformer } from "entities/attachments/graphql/attachmentEntitiesResponseTransformer"
import { patchAttachmentEntitiesType } from "entities/attachments/graphql/patchAttachmentEntitiesType"
import { clarificationRequestResponseTransformer } from "entities/clarification-requests/lib/clarificationRequestResponseTransformer"
import {
  patchDimensioningOption,
  patchProductConfigurationResponse
} from "entities/dim-configuration/graphql/patchDimensioningPhysicalData"
import {
  patchExternalDimensioningResult,
  patchExternalDimensioningTechnicalData
} from "entities/dim-configuration/graphql/patchExternalDimensioningTechnicalData"
import { productConfigurationModelsResponseTransformer } from "entities/dim-configuration/graphql/productConfigurationModelsResponseTransformer"
import { productConfigurationResponseTransformer } from "entities/dim-configuration/graphql/productConfigurationResponseTransformer"
import {
  favoritesProductPositionsResponseTransformer,
  favoritesResponseTransformer
} from "entities/favorites/graphql/favoritesResponseTransformer"
import { floorResponseTransformer } from "entities/floors/lib/floorResponseTransformer"
import { patchFollowUpConfigurationType } from "entities/follow-up-configuration/graphql/patchFollowUpConfigurationType"
import { patchProductPosition } from "entities/product-positions/graphql/patchProductPosition"
import { productPositionResponseTransformer } from "entities/product-positions/graphql/productPositionResponseTransformer"
import { productInformationResponseTransformer } from "entities/products/graphql/productInformationResponseTransformer"
import { patchProjectParentType } from "entities/projects/graphql/patchProjectParentType"
import { projectHistoryResponseTransformer } from "entities/projects/graphql/project-history/projectHistoryResponseTransformer"
import { projectImportResponseTransformer } from "entities/projects/graphql/projectImportResponseTransformer"
import { projectResponseTransformer } from "entities/projects/graphql/projectResponseTransformer"
import {
  allUserPermissionsResponseTransformer,
  userPermissionsResponseTransformer
} from "entities/projects/graphql/userPermissionsTransformer"
import { roomResponseTransformer } from "entities/rooms/lib/roomResponseTransformer"
import { kpiDashboardResponseTransformer } from "features/kpi/graphql/kpiResponseTransformer"

export const apolloRestLink = new RestLink({
  uri: import.meta.env.VITE_API_BASE,
  typePatcher: {
    AttachmentEntities: patchAttachmentEntitiesType,
    Project: patchProjectParentType,
    Room: patchProjectParentType,
    Floor: patchProjectParentType,
    ProductPosition: patchProductPosition,
    ProductConfigurationResponse: patchProductConfigurationResponse,
    DimensioningOption: patchDimensioningOption,
    ExternalDimensioningResult: patchExternalDimensioningResult,
    ExternalDimensioningTechnicalData: patchExternalDimensioningTechnicalData,
    FollowUpConfiguration: patchFollowUpConfigurationType
  },
  bodySerializers,
  responseTransformer,
  endpoints: {
    userPermissions: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: userPermissionsResponseTransformer
    },
    allUserPermissions: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: allUserPermissionsResponseTransformer
    },
    favorites: {
      uri: `${import.meta.env.VITE_API_BASE}/project-list/favorites`,
      responseTransformer: favoritesResponseTransformer
    },
    favoritesProductPositions: {
      uri: `${
        import.meta.env.VITE_API_BASE
      }/project-list/favorites/project-positions`,
      responseTransformer: favoritesProductPositionsResponseTransformer
    },
    price: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: priceResponseTransformer
    },
    productPrice: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: productPriceResponseTransformer
    },
    productConfiguration: {
      uri: `${import.meta.env.VITE_API_BASE}/product-configuration/models`,
      responseTransformer: productConfigurationResponseTransformer
    },
    productConfigurationModels: {
      uri: `${import.meta.env.VITE_API_BASE}/product-configuration/models`,
      responseTransformer: productConfigurationModelsResponseTransformer
    },
    productInformation: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: productInformationResponseTransformer
    },
    project: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: projectResponseTransformer
    },
    health: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: healthResponseTransformer
    },
    productPosition: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: productPositionResponseTransformer
    },
    room: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: roomResponseTransformer
    },
    floor: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: floorResponseTransformer
    },
    deleteProjectChildren: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: deleteMutationResponseTransformer
    },
    dimensioningDataExchange: {
      uri: import.meta.env.VITE_DIM_EXCHANGE_URL,
      responseTransformer: noTransform
    },
    userProfile: {
      uri: `${import.meta.env.VITE_JR_API_BASE}/neoletter/instances/${
        import.meta.env.VITE_SCRIVITO_TENANT
      }/my/profile`,
      responseTransformer: noTransform
    },
    userSpace: {
      uri: `${import.meta.env.VITE_API_BASE}/user-space`,
      responseTransformer: userSpaceResponseTransformer
    },
    kpi: {
      uri: `${import.meta.env.VITE_API_BASE}/dashboard`,
      responseTransformer: kpiDashboardResponseTransformer
    },
    delete: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: deleteMutationResponseTransformer
    },
    clarificationRequest: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: clarificationRequestResponseTransformer
    },
    dimConfiguration: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: noTransform
    },
    followUpConfiguration: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: noTransform
    },
    deliveryTimeActiveImport: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: noTransform
    },
    projectHistory: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: projectHistoryResponseTransformer
    },
    attachmentEntities: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: attachmentEntitiesResponseTransformer
    },
    projectImport: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: projectImportResponseTransformer
    },
    attachmentAcceptedFileTypes: {
      uri: import.meta.env.VITE_API_BASE,
      responseTransformer: attachmentAcceptedFileTypesResponseTransformer
    }
  },
  customFetch: request
})

const overwriteWithIncoming = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  merge(_: any, incoming: any) {
    return incoming
  }
}

export const apolloCache = new InMemoryCache({
  typePolicies: {
    Room: {
      keyFields: ["id"],
      fields: {
        children: overwriteWithIncoming
      }
    },
    Floor: {
      keyFields: ["id"],
      fields: {
        children: overwriteWithIncoming
      }
    },
    Project: {
      keyFields: ["id"],
      fields: {
        children: overwriteWithIncoming,
        specialPriceGroups: overwriteWithIncoming
      }
    },

    ProductPosition: {
      fields: {
        addons: overwriteWithIncoming,
        dimensioning: overwriteWithIncoming,
        references: overwriteWithIncoming
      }
    },
    UserPermission: { keyFields: ["resourceId", "resourceType"] },
    ClarificationRequest: { keyFields: ["id"] },
    Query: {
      fields: {
        productPositions: overwriteWithIncoming,
        projects: overwriteWithIncoming,
        teamInvitations: overwriteWithIncoming,

        project: {
          read(existing, { args, toReference }) {
            return (
              existing ?? toReference({ __typename: "Project", id: args?.id })
            )
          }
        },
        floor: {
          read(existing, { args, toReference }) {
            return (
              existing ?? toReference({ __typename: "Floor", id: args?.id })
            )
          }
        },
        room: {
          read(existing, { args, toReference }) {
            return existing ?? toReference({ __typename: "Room", id: args?.id })
          }
        },
        projectPermissions: {
          read(existing, { args, toReference }) {
            return (
              existing ??
              toReference({
                __typename: "UserPermission",
                resourceType: "projects",
                resourceId: args?.projectID
              })
            )
          }
        },

        projectHistory: {
          keyArgs: ["resourceType", "id", "from", "to"],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          merge(_: any, incoming: any, { args }: any) {
            const existingEntries = _?.entries ?? []
            const incomingEntries = incoming?.entries ?? []
            if (args?.before) {
              return {
                ...incoming,
                entries: [...incomingEntries, ...existingEntries],
                links: {
                  prev: incoming.links?.prev ?? null,
                  next: _?.links?.next ?? incoming.links?.next ?? null
                }
              }
            }
            return {
              ...incoming,
              entries: [...existingEntries, ...incomingEntries],
              links: {
                prev: _?.links?.prev ?? incoming.links?.prev ?? null,
                next: incoming.links?.next ?? null
              }
            }
          }
        },
        clarificationRequest: {
          read(existing, { args, toReference }) {
            return (
              existing ??
              toReference({
                __typename: "ClarificationRequest",
                id: `${args?.resourceType}:${args?.id}`
              })
            )
          }
        }
      }
    }
  }
})

const errorLink = onError(({ graphQLErrors, operation }) => {
  // Network errors are logged and handled in fetchWithRetries
  // GraphQL errors need to be handled here
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      new GraphQLResponseError({
        message,
        operationName: operation.operationName,
        extensions
      }).notify()
    })
  }
})

// Setup your client
export const apolloClient = new ApolloClient({
  uri: import.meta.env.PROJECTS_API_ENDPOINT,
  cache: apolloCache,
  link: errorLink.concat(apolloRestLink)
})
